import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Convert VAPID key from base64 URL safe to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const PushNotificationManager = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [permission, setPermission] = useState('default');

  useEffect(() => {
    // Check if push notifications are supported
    const checkSupport = async () => {
      if ('serviceWorker' in navigator && 'PushManager' in window && typeof window.Notification !== 'undefined') {
        setIsSupported(true);
        
        // Check current permission
        setPermission(window.Notification.permission);
        
        // Check if already subscribed
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          setIsSubscribed(!!subscription);
        } catch (error) {
          console.error('Error checking subscription:', error);
        }
      }
    };
    
    checkSupport();
  }, []);

  const subscribeToPush = async () => {
    setIsLoading(true);
    
    try {
      // Request notification permission
      if (typeof window.Notification === 'undefined') {
        toast.error('Notifications are not supported on this device');
        setIsLoading(false);
        return;
      }
      const permissionResult = await window.Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        toast.error('Notification permission denied');
        setIsLoading(false);
        return;
      }
      
      // Get VAPID public key from backend
      const token = localStorage.getItem('token');
      const vapidResponse = await axios.get(`${API_BASE_URL}/push/vapid-public-key`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const vapidPublicKey = vapidResponse.data.publicKey;
      if (!vapidPublicKey) {
        toast.error('Push notifications not configured on server');
        setIsLoading(false);
        return;
      }
      
      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      
      // Send subscription to backend
      await axios.post(
        `${API_BASE_URL}/push/subscribe`,
        subscription.toJSON(),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setIsSubscribed(true);
      toast.success('Push notifications enabled!');
    } catch (error) {
      console.error('Error subscribing to push:', error);
      toast.error('Failed to enable push notifications');
    } finally {
      setIsLoading(false);
    }
  };

  const unsubscribeFromPush = async () => {
    setIsLoading(true);
    
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Unsubscribe from push
        await subscription.unsubscribe();
        
        // Notify backend
        const token = localStorage.getItem('token');
        await axios.post(
          `${API_BASE_URL}/push/unsubscribe`,
          { endpoint: subscription.endpoint },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      setIsSubscribed(false);
      toast.success('Push notifications disabled');
    } catch (error) {
      console.error('Error unsubscribing from push:', error);
      toast.error('Failed to disable push notifications');
    } finally {
      setIsLoading(false);
    }
  };

  const testNotification = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/push/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast.error('Failed to send test notification');
    }
  };

  if (!isSupported) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground text-sm">
        <BellOff className="w-4 h-4" />
        <span>Push notifications not supported</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {isSubscribed ? (
            <div className="flex items-center gap-2 text-green-500">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Notifications Enabled</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <BellOff className="w-5 h-5" />
              <span>Notifications Disabled</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {isSubscribed && (
            <Button
              variant="outline"
              size="sm"
              onClick={testNotification}
              disabled={isLoading}
            >
              Test
            </Button>
          )}
          
          <Button
            variant={isSubscribed ? "destructive" : "default"}
            size="sm"
            onClick={isSubscribed ? unsubscribeFromPush : subscribeToPush}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isSubscribed ? (
              <>
                <BellOff className="w-4 h-4 mr-2" />
                Disable
              </>
            ) : (
              <>
                <Bell className="w-4 h-4 mr-2" />
                Enable
              </>
            )}
          </Button>
        </div>
      </div>
      
      {permission === 'denied' && (
        <p className="text-sm text-red-500">
          Notifications are blocked. Please enable them in your browser settings.
        </p>
      )}
      
      <p className="text-sm text-muted-foreground">
        Get notified about new order assignments, lead assignments, and reminders.
      </p>
    </div>
  );
};

export default PushNotificationManager;
